import { Box, Card, Tooltip, Typography, useTheme } from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CategoryIcon from '@mui/icons-material/Category';
import GetAppIcon from '@mui/icons-material/GetApp';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router';
import { mindtimeWazuhComplianceApi } from 'src/api/mindtime/wazuhComplianceApi';
import { Button, Table } from 'src/common';
import { getSecurityComplianceDetailHref } from 'src/mindtime/config/securityNav';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { guardEdrPreviewAction } from 'src/mindtime/utils/edrPreviewGuard';
import { getAgentCompliancePreview } from '../../preview/compliance';
import { getEndpointPreviewRow } from '../../preview/endpoints';
import type {
  WazuhComplianceAgentSummaryData,
  WazuhComplianceControlItem,
  WazuhComplianceControlsQuery,
} from 'src/types/mindtime/wazuhCompliance';
import type { Column, TableData } from 'src/types/table';
import logger from 'src/utils/logger';
import {
  COMPLIANCE_CATEGORY_FILTER_OPTIONS,
  COMPLIANCE_FRAMEWORK_FILTER_OPTIONS,
} from '../../configuration-compliance/complianceFrameworkOptions';
import {
  complianceStatusToTone,
  getComplianceControlId,
  toComplianceTitle,
} from '../../configuration-compliance/complianceDetailModel';
import {
  EDR_COLORS,
  EdrDataCard,
  EdrDataToolbar,
  EdrDateTime,
  EdrStatusPill,
  edrMutedStyle,
  formatEdrDateTime,
  type EdrStatusTone,
  type EdrToolbarFilter,
  formatEdrNumber,
}  from '../../shared/edrPrimitives';
import {
  SecurityDashboardCard,
  securitySecurityScoreCardPadding,
  securitySecurityScoreCardSx,
  securitySecurityScoreHeaderTitleStyle,
  securitySecurityScoreImpactBoxSx,
} from '../../shared/securityDashboardCard';
import {
  securityDashboardFlexGrowSx,
  securityDashboardFlexRailSx,
  securityDashboardFlexRowSx,
} from '../../shared/securityDashboardLayout';
import { useSecurityDashboardGrid } from '../../shared/useSecurityDashboardGrid';
import { COLOR_BLUE, COLOR_RED } from './shared/endpointTableTab';

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  { label: 'Pass', value: 'pass' },
  { label: 'Fail', value: 'fail' },
  { label: 'Needs Review', value: 'needs_review' },
];

type Filters = { framework?: string; status?: string; category?: string };

const DEFAULT_FILTERS: Filters = { status: 'fail' };


const buildListQuery = (
  systemWazuhAgentId: string,
  page: number,
  pageSize: number,
  search: string,
  filters: Filters
): WazuhComplianceControlsQuery => ({
  systemWazuhAgentId,
  page,
  pageSize,
  ...(search.trim() ? { search: search.trim() } : {}),
  ...(filters.framework ? { framework: filters.framework } : {}),
  ...(filters.status ? { status: filters.status } : {}),
  ...(filters.category ? { category: filters.category } : {}),
});

const FrameworkTags: FC<{ values?: string[] }> = ({ values }) => {
  const items = (values ?? []).filter(Boolean);
  if (!items.length) return <span style={edrMutedStyle}>—</span>;
  return (
    <Box display="flex" flexWrap="wrap" style={{ gap: 4, maxWidth: 180 }}>
      {items.map((value) => (
        <span
          key={value}
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: 10,
            fontWeight: 600,
            background: EDR_COLORS.brand50,
            color: EDR_COLORS.brand600,
            border: `1px solid ${EDR_COLORS.brand200}`,
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </span>
      ))}
    </Box>
  );
};

const ComplianceStatColumn: FC<{
  label: string;
  value: number;
  tone: EdrStatusTone;
  pillLabel: string;
  showDivider: boolean;
}> = ({ label, value, tone, pillLabel, showDivider }) => (
  <Box
    style={{
      minWidth: 0,
      padding: '0 20px',
      borderLeft: showDivider ? '1px solid #EDF2F7' : 'none',
    }}
  >
    <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 10 }}>
      <Typography
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.4px',
          color: '#A0AEC0',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
      <ImportExportIcon style={{ fontSize: 16, color: COLOR_BLUE, flexShrink: 0 }} />
    </Box>
    <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap' }}>
      <Typography style={{ fontSize: 26, fontWeight: 700, color: COLOR_RED, lineHeight: 1.1 }}>
        {formatEdrNumber(value)}
      </Typography>
      <EdrStatusPill tone={tone} label={pillLabel} dot={false} />
    </Box>
  </Box>
);

const ComplianceScoreCard: FC<{ percent: number }> = ({ percent }) => {
  const primaryColor = useTheme().palette.primary.main;

  return (
    <Card
      sx={securitySecurityScoreCardSx}
      style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}
    >
      <Box sx={{ padding: securitySecurityScoreCardPadding }}>
        <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 8 }}>
          <Box display="flex" alignItems="center" style={{ color: primaryColor, lineHeight: 0 }}>
            <ShowChartIcon style={{ fontSize: 18 }} />
          </Box>
          <Typography
            component="span"
            style={{ ...securitySecurityScoreHeaderTitleStyle, color: primaryColor }}
          >
            Compliance Score
          </Typography>
        </Box>
          <Typography
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 24,
              fontWeight: 700,
              lineHeight: 1.1,
              color: '#1A202C',
            marginBottom: 8,
          }}
        >
          {formatEdrNumber(percent)}%
          </Typography>
        <Box sx={securitySecurityScoreImpactBoxSx} display="flex" alignItems="center">
          <Typography
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 11,
              lineHeight: '16px',
              color: '#4A5568',
            }}
          >
            Overall configuration compliance for this endpoint
          </Typography>
        </Box>
      </Box>
    </Card>
  );
};

type EndpointComplianceTabProps = {
  systemWazuhAgentId: string;
};

const EndpointComplianceTab: FC<EndpointComplianceTabProps> = ({ systemWazuhAgentId }) => {
  const navigate = useNavigate();
  const { isMdDown, isSmDown } = useSecurityDashboardGrid();
  const { wait, isPreviewMode } = useEdrDataGate('endpoints');
  const { canWidget } = useSubscriptionModulePermissions(
    EDR_SOLUTION_ID,
    'configuration-compliance',
  );
  const canSummary = canWidget('summary');
  /** Score card shares the sidebar `summary` entitlement (no separate endpoint widget). */
  const canScore = canSummary;
  const canList = canWidget('list');
  const agentId = systemWazuhAgentId.trim();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [listLoading, setListLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [summary, setSummary] = useState<WazuhComplianceAgentSummaryData | null>(null);
  const [items, setItems] = useState<WazuhComplianceControlItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const openComplianceDetail = useCallback(
    (item: WazuhComplianceControlItem) => {
      const id = getComplianceControlId(item);
      if (!id) return;
      navigate(getSecurityComplianceDetailHref(id), {
        state: { record: item },
      });
    },
    [navigate]
  );

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
        setPage(0);
      }, 300),
    []
  );

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  useEffect(() => {
    if (!agentId) {
      setSummary(null);
      return undefined;
    }
    if (wait) return undefined;
    if (isPreviewMode) {
      const endpointLabel =
        getEndpointPreviewRow(agentId)?.endpoint ?? getEndpointPreviewRow(agentId)?.name ?? '';
      setSummary(
        canSummary || canScore ? getAgentCompliancePreview(endpointLabel).summary : null,
      );
      return undefined;
    }

    if (!canSummary && !canScore) {
      setSummary(null);
      return undefined;
    }

    let cancelled = false;
    mindtimeWazuhComplianceApi
      .getAgentSummary(agentId)
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'EndpointComplianceTab.summary');
          setSummary(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, wait, isPreviewMode, canSummary, canScore]);

  useEffect(() => {
    if (!agentId) {
      setItems([]);
      setTotalRecords(0);
      setListLoading(false);
      return undefined;
    }
    if (wait) {
      setListLoading(true);
      return undefined;
    }
    if (isPreviewMode) {
      if (!canList) {
        setItems([]);
        setTotalRecords(0);
        setListLoading(false);
        return undefined;
      }
      const endpointLabel =
        getEndpointPreviewRow(agentId)?.endpoint ?? getEndpointPreviewRow(agentId)?.name ?? '';
      const preview = getAgentCompliancePreview(endpointLabel);
      setItems(preview.items);
      setTotalRecords(preview.items.length);
      setListLoading(false);
      return undefined;
    }

    if (!canList) {
      setItems([]);
      setTotalRecords(0);
      setListLoading(false);
      return undefined;
    }

    let cancelled = false;
    setListLoading(true);
    mindtimeWazuhComplianceApi
      .getControls(buildListQuery(agentId, page, rowsPerPage, search, filters))
      .then((data) => {
        if (cancelled) return;
        setItems(data?.items ?? []);
        setTotalRecords(data?.totalRecords ?? 0);
        setListLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        logger(error, 'error', 'EndpointComplianceTab.list');
        setItems([]);
        setTotalRecords(0);
        setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, page, rowsPerPage, search, filters.framework, filters.status, filters.category, wait, isPreviewMode, canList]);

  const rows = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        id: getComplianceControlId(item) || item.checkId,
      })),
    [items]
  );

  const updatedLabel = summary?.meta?.lastSyncedAt
    ? `Updated: ${formatEdrDateTime(summary.meta.lastSyncedAt)}`
    : 'Updated: Just now';

  const setFilter = (key: keyof Filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
    setPage(0);
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchInput('');
    setSearch('');
    setPage(0);
  };

  const handleExport = useCallback(() => {
    if (!agentId) return;
    if (guardEdrPreviewAction(isPreviewMode, 'previewExportBlocked')) return;
    setExporting(true);
    mindtimeWazuhComplianceApi
      .export(buildListQuery(agentId, 0, rowsPerPage, search, filters))
      .then(({ blob, filename }) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename?.trim() || 'compliance-controls-export.csv');
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Export downloaded');
      })
      .catch((error) => {
        logger(error, 'error', 'EndpointComplianceTab.export');
        toast.error(error instanceof Error ? error.message : 'Export failed');
      })
      .finally(() => setExporting(false));
  }, [agentId, rowsPerPage, search, filters, isPreviewMode]);

  const toolbarFilters: EdrToolbarFilter[] = useMemo(
    () => [
      {
        id: 'framework',
        label: 'Framework',
        icon: <AccountTreeIcon style={{ fontSize: 14 }} />,
        options: COMPLIANCE_FRAMEWORK_FILTER_OPTIONS,
        value: filters.framework ?? '',
        multiple: true,
        onChange: (value) => setFilter('framework', value),
      },
      {
        id: 'status',
        label: 'Status',
        icon: <TrendingUpIcon style={{ fontSize: 14 }} />,
        options: STATUS_OPTIONS,
        value: filters.status ?? '',
        multiple: true,
        onChange: (value) => setFilter('status', value),
      },
      {
        id: 'category',
        label: 'Category',
        icon: <CategoryIcon style={{ fontSize: 14 }} />,
        options: COMPLIANCE_CATEGORY_FILTER_OPTIONS,
        value: filters.category ?? '',
        multiple: true,
        onChange: (value) => setFilter('category', value),
      },
    ],
    [filters]
  );

  const columns: Column[] = useMemo(
    () => [
      {
        id: 'title',
        label: 'Security Check',
        width: '32%',
        render: (row) => {
          const item = row as unknown as WazuhComplianceControlItem;
          const title = item.title?.trim() || '—';
          return (
            <Tooltip title={title !== '—' ? title : ''} placement="top" enterDelay={300}>
              <Typography noWrap style={{ fontSize: 12, fontWeight: 600, color: '#1E293B', maxWidth: '100%' }}>
                {title}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        id: 'framework',
        label: 'Framework',
        width: '20%',
        render: (row) => (
          <FrameworkTags values={(row as unknown as WazuhComplianceControlItem).framework} />
        ),
      },
      {
        id: 'status',
        label: 'Status',
        width: '14%',
        render: (row) => {
          const item = row as unknown as WazuhComplianceControlItem;
          return (
            <EdrStatusPill
              tone={complianceStatusToTone(item.status)}
              label={toComplianceTitle(item.status) || '—'}
            />
          );
        },
      },
      {
        id: 'category',
        label: 'Category',
        width: '16%',
        render: (row) => (
          <Typography noWrap style={{ fontSize: 12, color: '#334155', maxWidth: '100%' }}>
            {(row as unknown as WazuhComplianceControlItem).category?.trim() || '—'}
          </Typography>
        ),
      },
      {
        id: 'lastChecked',
        label: 'Last Checked',
        width: '18%',
        render: (row) => (
          <EdrDateTime value={(row as unknown as WazuhComplianceControlItem).lastChecked} />
        ),
      },
    ],
    []
  );

  const kpiColumns = isSmDown
    ? 'minmax(0, 1fr)'
    : isMdDown
      ? 'repeat(2, minmax(0, 1fr))'
      : 'repeat(3, minmax(0, 1fr))';

  const showSummaryRow = canSummary || canScore;

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16, marginTop: 8, minWidth: 0 }}>
      {showSummaryRow ? (
      <Box sx={securityDashboardFlexRowSx(isMdDown)}>
        {canSummary ? (
        <Box sx={securityDashboardFlexGrowSx}>
        <SecurityDashboardCard
          title="Compliance"
          icon={<VerifiedUserIcon style={{ fontSize: 18 }} />}
          headerRight={
            <Typography style={{ fontSize: 11, color: '#A0AEC0' }}>{updatedLabel}</Typography>
          }
          style={{ height: '100%', minWidth: 0 }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: kpiColumns, py: 2 }}>
            <ComplianceStatColumn
              label="Passed Controls"
              value={summary?.passedControls ?? 0}
              tone="success"
              pillLabel="Pass"
              showDivider={false}
            />
            <ComplianceStatColumn
              label="Failed Controls"
              value={summary?.failedControls ?? 0}
              tone="critical"
              pillLabel="Fail"
              showDivider={!isSmDown}
            />
            <ComplianceStatColumn
              label="Not Applicable"
              value={summary?.notApplicable ?? 0}
              tone="neutral"
              pillLabel="N/A"
              showDivider={!isMdDown}
            />
          </Box>
        </SecurityDashboardCard>
        </Box>
        ) : null}

        {canScore ? (
        <Box sx={securityDashboardFlexRailSx(isMdDown, canSummary)}>
        <ComplianceScoreCard percent={summary?.complianceScore ?? 0} />
        </Box>
        ) : null}
      </Box>
      ) : null}

      {canList ? (
      <EdrDataCard
        toolbar={(
          <EdrDataToolbar
            searchValue={searchInput}
            onSearchChange={(value) => {
              setSearchInput(value);
              debouncedSetSearch(value);
            }}
            searchPlaceholder="Search compliance controls…"
            filters={toolbarFilters}
            onReset={handleReset}
            trailing={(
              <Button
                variant="outlined"
                color="primary"
                startIcon={<GetAppIcon />}
                style={{ textTransform: 'none' }}
                loading={exporting}
                onClick={handleExport}
              >
                Export
              </Button>
            )}
          />
        )}
      >
        <Table
          columns={columns}
          data={rows as unknown as TableData[]}
          loading={listLoading}
          showPagination
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={totalRecords}
          onPageChange={setPage}
          onRowsPerPageChange={(next) => {
            setRowsPerPage(next);
            setPage(0);
          }}
          onRowClick={(row) => openComplianceDetail(row as unknown as WazuhComplianceControlItem)}
          TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          MUITableProps={{ sx: { tableLayout: 'fixed', width: '100%' } }}
        />
      </EdrDataCard>
      ) : null}
    </Box>
  );
};

export default EndpointComplianceTab;
